import React from 'react';
import { ArrowLeft, CheckCircle, Shield, Building2, Award, FileCheck, CreditCard, Landmark } from 'lucide-react';
import { Link } from 'react-router-dom';

const Compliance: React.FC = () => {
  const certifications = [
    {
      icon: <Award className="h-8 w-8 text-blue-600" />,
      title: "Valor Certified Installer",
      description: "Certified by Valor to install and configure their payment processing devices. We meet all Valor technical and security requirements."
    },
    {
      icon: <Building2 className="h-8 w-8 text-green-600" />,
      title: "Trusted by Major Banks",
      description: "Partnered with and trusted by major financial institutions including Chase, Bank of America, Wells Fargo, and regional banks nationwide."
    },
    {
      icon: <Shield className="h-8 w-8 text-purple-600" />,
      title: "PCI DSS Compliant",
      description: "All Valor devices we install are PCI DSS compliant, ensuring secure payment processing for your business."
    },
    {
      icon: <FileCheck className="h-8 w-8 text-orange-600" />,
      title: "Device Certification",
      description: "Every Valor device we install is certified, tested, and verified to meet industry security standards before deployment."
    }
  ];

  const bankPartners = [
    "Chase",
    "Bank of America",
    "Wells Fargo",
    "Citibank",
    "U.S. Bank",
    "PNC Bank"
  ];

  const valorDevices = [
    "Valor POS Terminals",
    "Valor Mobile Readers",
    "Valor Countertop Systems",
    "Valor Integrated Solutions"
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-8">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>
        
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="bg-blue-100 p-4 rounded-full">
              <Award className="h-16 w-16 text-blue-600" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Compliance & Certifications</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            As a Valor certified installer trusted by major banks, we ensure all payment devices meet the highest security and compliance standards.
          </p>
        </div>

        {/* Certifications Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {certifications.map((cert, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {cert.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{cert.title}</h3>
                  <p className="text-gray-600 text-sm">{cert.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Valor Certification Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <div className="flex items-center gap-3 mb-6">
            <CreditCard className="h-8 w-8 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Valor Device Certification</h2>
          </div>
          <p className="text-gray-700 mb-6">
            We are certified by Valor to install, configure, and maintain their payment processing devices. Every device we install is:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {valorDevices.map((device, index) => (
              <div key={index} className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                <span className="text-gray-900 font-medium">{device}</span>
              </div>
            ))}
          </div>
          <div className="bg-blue-50 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Our Valor Certification Includes:</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Certified installation and setup procedures</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Ongoing technical support and maintenance</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Compliance verification for all devices</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Regular firmware updates and security patches</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bank Partnerships */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-lg shadow-lg p-8 mb-12">
          <div className="text-center mb-8">
            <Landmark className="h-12 w-12 mx-auto mb-4 text-white" />
            <h2 className="text-3xl font-bold mb-4">Trusted by Major Banks</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              We are a trusted partner of major financial institutions nationwide. Our certifications and compliance standards meet the rigorous requirements set by these leading banks.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {bankPartners.map((bank, index) => (
              <div key={index} className="bg-white/10 rounded-lg p-4 text-center backdrop-blur-sm">
                <Building2 className="h-8 w-8 mx-auto mb-2 text-white" />
                <p className="text-sm font-medium">{bank}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-gray-300 text-sm">
              Our partnerships with these institutions demonstrate our commitment to security, compliance, and reliability.
            </p>
          </div>
        </div>

        {/* Compliance Standards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Device Compliance</h2>
            <p className="text-gray-700 mb-4">
              All Valor devices we install and maintain are fully compliant with industry standards:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>PCI DSS Level 1</strong> compliance for all payment devices</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>EMV chip card</strong> technology support</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Contactless payment</strong> (NFC) enabled</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Encrypted PIN pad</strong> for secure transactions</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Installation Standards</h2>
            <p className="text-gray-700 mb-4">
              Our certified installation process ensures:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Pre-installation testing</strong> and verification</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Proper network configuration</strong> and security setup</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Staff training</strong> on device operation</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Ongoing support</strong> and maintenance services</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Why Banks Trust Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Award className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Certified Expertise</h3>
              <p className="text-gray-600 text-sm">Valor certified installers with extensive training and experience</p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Shield className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Security First</h3>
              <p className="text-gray-600 text-sm">All devices meet bank-level security and compliance requirements</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <FileCheck className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Proven Track Record</h3>
              <p className="text-gray-600 text-sm">Years of successful installations for major financial institutions</p>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-blue-50 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions About Compliance?</h2>
          <p className="text-gray-700 mb-6">
            Our compliance team can provide detailed information about our Valor certifications and bank partnerships.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:simran@simplprocessing.com" className="bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors inline-flex items-center justify-center gap-2">
              <span>Email Compliance Team</span>
            </a>
            <a href="tel:+1201-846-0591" className="bg-white text-gray-900 border-2 border-gray-900 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors inline-flex items-center justify-center gap-2">
              <span>Call (201) 846-0591</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Compliance;

